import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ReviewWidget from '../components/ReviewWidget';
import CoopSavingsWidget from '../components/CoopSavingsWidget';
import ShareSlipModal, { buildYearlyShareData } from '../components/ShareSlipModal';
import './Dashboard.css';

const thaiMonths = [
    '', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
    'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
];

const thaiMonthsFull = [
    '', 'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
];

function formatNumber(num) {
    if (num === null || num === undefined) return '0.00';
    return Number(num).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// นับถอยหลังวันเงินเดือนออก (เดินจริงระดับวินาที)
// นับไปยังเวลาที่เงินมักเข้าบัญชี (ตี 2) ของวันเงินเดือนออก
function PaymentCountdown({ paymentDate }) {
    const TRANSFER_HOUR = 2; // เงินมักเข้าบัญชีช่วงตี 2 – ตี 3
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        // อัปเดตทุก 1 วินาที
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);

    if (!paymentDate) return null;

    const pd = new Date(paymentDate);
    // สร้างเวลาเป้าหมายเป็น "ตี 2 ของวันเงินเดือนออก" ตามเวลาท้องถิ่น
    const target = new Date(pd.getFullYear(), pd.getMonth(), pd.getDate(), TRANSFER_HOUR, 0, 0).getTime();
    const diffMs = target - now;

    // เลยเวลาโอนแล้ว (เงินเข้าบัญชีแล้ว)
    if (diffMs <= 0) {
        return (
            <div className="payment-countdown-wrap">
                <span className="payment-countdown soon">เงินเข้าบัญชีแล้ว</span>
                <span className="payment-countdown-sub">ตรวจสอบยอดได้เลย</span>
            </div>
        );
    }

    const totalSeconds = Math.floor(diffMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (n) => String(n).padStart(2, '0');

    const mainText = days >= 1
        ? `อีก ${days} วัน ${pad(hours)} ชั่วโมง ${pad(minutes)} นาที ${pad(seconds)} วินาที`
        : `อีก ${hours} ชั่วโมง ${pad(minutes)} นาที ${pad(seconds)} วินาที`;

    return (
        <div className="payment-countdown-wrap">
            <span className={`payment-countdown countdown-clock ${days <= 7 ? 'soon' : ''}`}>{mainText}</span>
            <span className="payment-countdown-sub">เงินมักเข้าบัญชีช่วงตี 2 – ตี 3</span>
        </div>
    );
}

// ประกาศข่าวสาร: ระบบลงเวลา / ตารางเวร เวอร์ชันใหม่ (iService /hr) ชวนผูกบัญชีใช้แทนระบบเดิม
// - กด ✕ = ยุบเหลือปุ่มเล็ก กดปุ่มเล็กเพื่อเปิดกลับมาดูได้ (จำสถานะไว้ในเครื่อง)
// - เปลี่ยนเนื้อหาประกาศเมื่อไร ให้เปลี่ยน NEWS_DISMISS_KEY ด้วย ประกาศใหม่จะโผล่ให้ทุกคนแม้เคยปิดไปแล้ว
const HR_NOTICE_URL = 'https://iservice.sisakethospital.com/hr';
const NEWS_DISMISS_KEY = 'news_notice_dismissed_hr_v1';
// เก็บสถิติคลิกปุ่ม "ผูกบัญชีเลย" (server แจ้งเข้ากลุ่ม LINE เมื่อเป็นคลิกแรกของคนนั้น) — ไม่รอผล ไม่ขวางการเปิดลิงก์
const trackHrLinkClick = () => { api.post('/promo/click', { key: 'hr_link' }).catch(() => {}); };
const ISERVICE_COLLAPSE_KEY = 'iservice_card_collapsed';

function readLocalFlag(key) {
    try { return localStorage.getItem(key) === '1'; } catch { return false; }
}
function writeLocalFlag(key, value) {
    try {
        if (value) localStorage.setItem(key, '1');
        else localStorage.removeItem(key);
    } catch { /* ignore */ }
}

function NewsNotice() {
    const [dismissed, setDismissed] = useState(() => readLocalFlag(NEWS_DISMISS_KEY));

    const setDismissedPersist = (value) => {
        writeLocalFlag(NEWS_DISMISS_KEY, value);
        setDismissed(value);
    };

    const ICON = (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
        </svg>
    );

    // ผู้ใช้กด ✕ ไว้ เหลือปุ่มเล็กให้กดเปิดกลับมาดู
    if (dismissed) {
        return (
            <button
                type="button"
                className="news-notice-pill"
                onClick={() => setDismissedPersist(false)}
                title="แสดงประกาศระบบลงเวลาใหม่"
            >
                {ICON}
                ระบบลงเวลาใหม่ ผูกบัญชีได้แล้ว
            </button>
        );
    }

    return (
        <div className="news-notice-card">
            <div className="news-notice-icon">{ICON}</div>
            <div className="news-notice-content">
                <div className="news-notice-info">
                    <span className="news-notice-tag">ข่าวสาร</span>
                    <span className="news-notice-headline">ระบบลงเวลา / ตารางเวร เวอร์ชันใหม่ เปิดให้ใช้แล้ว</span>
                    <span className="news-notice-note">
                        ดูเวลาเข้า-ออก ตารางเวร และสรุป OT ของคุณได้เร็วขึ้น ใช้ข้อมูลชุดเดียวกับระบบเดิม
                    </span>
                    <span className="news-notice-note">
                        <b>วิธีใช้:</b> เข้าสู่ระบบ iService แล้วผูกบัญชีด้วยรหัสผู้ใช้และรหัสผ่านของระบบลงเวลาเดิม ทำครั้งเดียว
                    </span>
                </div>
                <div className="news-notice-actions">
                    <span className="news-notice-status">ใช้แทนระบบเดิมได้ทันที</span>
                    <a
                        href={HR_NOTICE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="news-notice-btn"
                        onClick={trackHrLinkClick}
                    >
                        ผูกบัญชีเลย
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                            <polyline points="15 3 21 3 21 9"/>
                            <line x1="10" y1="14" x2="21" y2="3"/>
                        </svg>
                    </a>
                </div>
            </div>
            <button
                type="button"
                className="news-notice-close"
                onClick={() => setDismissedPersist(true)}
                title="ซ่อนประกาศ"
                aria-label="ซ่อนประกาศ"
            >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
            </button>
        </div>
    );
}

// การ์ดประชาสัมพันธ์ระบบแจ้งซ่อมคอมพิวเตอร์ (iService) ย่อ/ขยายได้ จำสถานะไว้ในเครื่อง
function IServiceCard() {
    const [collapsed, setCollapsed] = useState(() => readLocalFlag(ISERVICE_COLLAPSE_KEY));

    const toggle = () => {
        const next = !collapsed;
        writeLocalFlag(ISERVICE_COLLAPSE_KEY, next);
        setCollapsed(next);
    };

    const ICON = (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
            <line x1="8" y1="21" x2="16" y2="21"/>
            <line x1="12" y1="17" x2="12" y2="21"/>
        </svg>
    );

    if (collapsed) {
        return (
            <div className="iservice-card collapsed">
                <div className="iservice-icon">{ICON}</div>
                <a
                    href="https://iservice.sisakethospital.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="iservice-content iservice-content-link"
                >
                    <span className="iservice-title">แจ้งซ่อมคอมพิวเตอร์ / IT — iservice.sisakethospital.com</span>
                </a>
                <button type="button" className="iservice-toggle" onClick={toggle} title="ขยาย" aria-label="ขยาย">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"/>
                    </svg>
                </button>
            </div>
        );
    }

    return (
        <div className="iservice-card">
            <div className="iservice-icon">{ICON}</div>
            <a
                href="https://iservice.sisakethospital.com"
                target="_blank"
                rel="noopener noreferrer"
                className="iservice-content iservice-content-link"
            >
                <span className="iservice-title">คอมพิวเตอร์ / เครื่องพิมพ์ / อินเทอร์เน็ต มีปัญหา? แจ้งซ่อมออนไลน์ได้ที่นี่</span>
                <span className="iservice-url">iservice.sisakethospital.com</span>
            </a>
            <a
                href="https://iservice.sisakethospital.com"
                target="_blank"
                rel="noopener noreferrer"
                className="iservice-btn"
            >
                แจ้งซ่อม
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
            </a>
            <button type="button" className="iservice-toggle" onClick={toggle} title="ย่อ" aria-label="ย่อ">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="18 15 12 9 6 15"/>
                </svg>
            </button>
        </div>
    );
}

function Dashboard() {
    const { user } = useAuth();
    const [salaries, setSalaries] = useState([]);
    const [years, setYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState(null);
    const [yearlyData, setYearlyData] = useState(null);
    const [showYearShare, setShowYearShare] = useState(false); // แชร์สรุปรายปีจากการ์ดรายรับรวม
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [paymentInfo, setPaymentInfo] = useState(null);
    const [reportingIssue, setReportingIssue] = useState(false);
    const [issueReported, setIssueReported] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    // Fetch yearly data when year changes
    useEffect(() => {
        if (selectedYear) {
            fetchYearlyData(selectedYear);
        }
    }, [selectedYear]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [salaryRes, yearsRes] = await Promise.all([
                api.get('/salary/my-salary'),
                api.get('/salary/available-years')
            ]);
            setSalaries(salaryRes.data);
            setYears(yearsRes.data);
            if (yearsRes.data.length > 0) {
                setSelectedYear(yearsRes.data[0]);
            }

            // ดึงข้อมูลวันเงินเดือนออก (สำหรับทุกคน)
            try {
                const paymentRes = await api.get('/salary/payment-dates');
                setPaymentInfo(paymentRes.data);
            } catch (e) {
                // ไม่แสดง error ถ้าไม่มีข้อมูลวันเงินเดือน
                console.log('No payment date info available');
            }
        } catch (err) {
            setError(err.response?.data?.error || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
        } finally {
            setLoading(false);
        }
    };

    const fetchYearlyData = async (year) => {
        try {
            const response = await api.get(`/salary/yearly-summary/${year}`);
            setYearlyData(response.data);
        } catch (err) {
            // ถ้าไม่มีข้อมูลก็ไม่แสดง error
            setYearlyData(null);
        }
    };

    // Helper: คำนวณยอดสุทธิ (ใช้ tr_net ถ้า cnet = 0)
    const getNetPay = (s) => {
        if (s.cnet && s.cnet > 0) return s.cnet;
        return s.tr_net || 0;
    };

    // Helper: คำนวณรายรับ (ใช้ inet ถ้า cinet = 0)
    const getIncome = (s) => {
        if (s.cinet && s.cinet > 0) return s.cinet;
        return s.inet || 0;
    };

    // Helper: คำนวณรายหัก (ใช้ enet ถ้า cenet = 0)
    const getDeduction = (s) => {
        if (s.cenet && s.cenet > 0) return s.cenet;
        return s.enet || 0;
    };

    // Helper: เดือนที่ยอดเป็น 0 ทั้งหมด = ยังไม่มีข้อมูลจริง (เจ้าหน้าที่กำลังจัดเตรียมข้อมูล)
    const isPreparing = (s) => getIncome(s) === 0 && getDeduction(s) === 0 && getNetPay(s) === 0;

    // คำนวณยอดรวมปี
    const yearlyTotal = salaries
        .filter(s => s.yyyy === selectedYear)
        .reduce((sum, s) => sum + getNetPay(s), 0);

    const yearlyIncome = salaries
        .filter(s => s.yyyy === selectedYear)
        .reduce((sum, s) => sum + getIncome(s), 0);

    const yearlyDeduction = salaries
        .filter(s => s.yyyy === selectedYear)
        .reduce((sum, s) => sum + getDeduction(s), 0);

    // มี record ของปีนี้ แต่ทุกเดือนยอดเป็น 0 = ทั้งปีกำลังจัดเตรียมข้อมูล (ต้นปี/ต้นเดือน)
    const yearRecords = salaries.filter(s => s.yyyy === selectedYear);
    const allPreparing = yearRecords.length > 0 && yearRecords.every(isPreparing);

    // แสดงยอดในการ์ดสรุป — ถ้าทั้งปียังไม่มีข้อมูล แสดง "กำลังจัดเตรียมข้อมูล" แทน 0.00
    const renderSummaryAmount = (value) => (
        allPreparing ? (
            <span className="summary-preparing">กำลังจัดเตรียมข้อมูล</span>
        ) : (
            <>
                <span className="summary-value">{formatNumber(value)}</span>
                <span className="summary-unit">บาท</span>
            </>
        )
    );

    // คำนวณยอดภาษีหัก ณ ที่จ่าย จาก detailByType (หารายการที่เกี่ยวกับภาษี)
    const getTaxData = () => {
        if (!yearlyData || !yearlyData.detailByType) return { withholdingTax: 0, taxRelief: 0 };

        // แยกรายการหัก (ตาม flag)
        const deductionItems = yearlyData.detailByType.filter(d => d.flag < 0);

        // หารายการภาษีหัก ณ ที่จ่าย (โดยทั่วไปจะมีคำว่า "ภาษี" หรือ "ณ ที่จ่าย")
        const withholdingTaxItem = deductionItems.find(item =>
            item.acc_name && (
                item.acc_name.includes('ภาษี') ||
                item.acc_name.includes('ณ ที่จ่าย') ||
                item.acc_name.toLowerCase().includes('tax')
            )
        );

        return {
            withholdingTax: withholdingTaxItem ? Math.abs(withholdingTaxItem.total_amt) : 0,
            taxInfo: yearlyData.taxInfo
        };
    };

    const taxData = getTaxData();

    // ฟังก์ชันแจ้งปัญหาข้อมูลไม่พบ
    const handleReportDataIssue = async () => {
        try {
            setReportingIssue(true);
            await api.post('/request/report-data-issue', {
                issue_type: 'no_salary_data',
                description: `ไม่พบข้อมูลเงินเดือนในระบบ - CID: ${user?.cid || 'N/A'}, ชื่อ: ${user?.fullname || 'N/A'}`
            });
            setIssueReported(true);
            alert('ส่งแจ้งปัญหาเรียบร้อยแล้ว ผู้ดูแลระบบจะตรวจสอบและแก้ไขให้โดยเร็ว');
        } catch (err) {
            alert(err.response?.data?.error || 'เกิดข้อผิดพลาดในการส่งแจ้งปัญหา');
        } finally {
            setReportingIssue(false);
        }
    };

    if (loading) {
        return (
            <div className="flex-center" style={{ padding: '60px 0' }}>
                <div className="spinner"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-error">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                {error}
            </div>
        );
    }

    // Format วันที่เป็นภาษาไทย
    const formatPaymentDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        const day = date.getDate();
        const month = thaiMonthsFull[date.getMonth() + 1];
        const year = date.getFullYear() + 543;
        return `${day} ${month} ${year}`;
    };

    return (
        <div className="dashboard">
            {/* Welcome Section */}
            <div className="welcome-section">
                <div className="welcome-text">
                    <h1>สวัสดี, {user?.fullname || 'คุณ'}</h1>
                    <p>ยินดีต้อนรับสู่ระบบ iMoney - ดูข้อมูลเงินเดือนของคุณ</p>
                </div>
                {user?.employee && (
                    <div className="employee-info">
                        <span className="badge badge-primary">{user.employee.positionname || user.employee.pos_name || 'พนักงาน'}</span>
                    </div>
                )}
            </div>

            {/* Payment Date Section - แสดงสำหรับทุกคน */}
            {paymentInfo?.nextPayment && (
                <div className="payment-date-section">
                    <div className="payment-date-card">
                        <div className="payment-date-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                <line x1="16" y1="2" x2="16" y2="6"/>
                                <line x1="8" y1="2" x2="8" y2="6"/>
                                <line x1="3" y1="10" x2="21" y2="10"/>
                            </svg>
                        </div>
                        <div className="payment-date-content">
                            <div className="payment-date-info">
                                <span className="payment-date-label">เงินเดือนออก:</span>
                                <span className="payment-date-value">
                                    {formatPaymentDate(paymentInfo.nextPayment.date)}
                                </span>
                                <span className="payment-date-month">
                                    ({thaiMonths[paymentInfo.nextPayment.month]} {paymentInfo.nextPayment.budget_year})
                                </span>
                            </div>
                            <PaymentCountdown paymentDate={paymentInfo.nextPayment.date} />
                        </div>
                    </div>
                </div>
            )}

            {/* ประกาศข่าวสาร: ระบบลงเวลา / ตารางเวร ใหม่ - อยู่ใต้วันเงินเดือนออก */}
            <div className="news-notice-section">
                <NewsNotice />
            </div>

            {/* ประชาสัมพันธ์ระบบแจ้งซ่อมคอมพิวเตอร์ (iService) ย่อ/ขยายได้ */}
            <IServiceCard />

            {/* Year Selector */}
            {years.length > 0 && (
                <div className="year-selector">
                    <label>เลือกปี:</label>
                    <div className="year-tabs">
                        {years.slice(0, 5).map(year => (
                            <button
                                key={year}
                                className={`year-tab ${selectedYear === year ? 'active' : ''}`}
                                onClick={() => setSelectedYear(year)}
                            >
                                {year + 543}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Summary Cards */}
            <div className="summary-cards">
                <div className="summary-card income">
                    <div className="summary-icon">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                    </div>
                    <div className="summary-content">
                        <span className="summary-label">รายรับรวม ปี {selectedYear + 543}</span>
                        {renderSummaryAmount(yearlyIncome)}
                    </div>
                    {/* แชร์สรุปรายได้ปีนี้เป็นรูปภาพ (มีข้อมูลรายปีแล้วเท่านั้น) */}
                    {yearlyData && yearlyIncome > 0 && (
                        <button
                            type="button"
                            className="summary-share-btn"
                            onClick={() => setShowYearShare(true)}
                            title={`แชร์สรุปรายได้ปี ${selectedYear + 543}`}
                            aria-label="แชร์สรุปรายได้ประจำปี"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7"/>
                                <polyline points="16 6 12 2 8 6"/>
                                <line x1="12" y1="2" x2="12" y2="15"/>
                            </svg>
                        </button>
                    )}
                </div>

                <div className="summary-card deduction">
                    <div className="summary-icon">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z"/>
                        </svg>
                    </div>
                    <div className="summary-content">
                        <span className="summary-label">รายหักรวม ปี {selectedYear + 543}</span>
                        {renderSummaryAmount(yearlyDeduction)}
                    </div>
                </div>

                <div className="summary-card net">
                    <div className="summary-icon">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
                        </svg>
                    </div>
                    <div className="summary-content">
                        <span className="summary-label">รับสุทธิรวม ปี {selectedYear + 543}</span>
                        {renderSummaryAmount(yearlyTotal)}
                    </div>
                </div>

                {/* Tax Card - แสดงเมื่อมีข้อมูลภาษี */}
                {taxData.withholdingTax > 0 && (
                    <div className="summary-card tax">
                        <div className="summary-icon">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14H6v-2h6v2zm4-4H6v-2h10v2zm0-4H6V7h10v2z"/>
                            </svg>
                        </div>
                        <div className="summary-content">
                            <span className="summary-label">ภาษีหัก ณ ที่จ่าย ปี {selectedYear + 543}</span>
                            <span className="summary-value">{formatNumber(taxData.withholdingTax)}</span>
                            <span className="summary-unit">บาท</span>
                        </div>
                    </div>
                )}

                {/* ยอดสหกรณ์ออมทรัพย์ — การ์ดในกลุ่มข้อมูลการเงินเดียวกัน แสดงเฉพาะคนที่บันทึกยอดไว้ */}
                <CoopSavingsWidget />
            </div>

            {/* Quick Actions - ลิงก์ดูข้อมูลสรุปรายปี */}
            <div className="quick-actions">
                <Link to={`/yearly/${selectedYear}`} className="action-card">
                    <div className="action-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                            <line x1="16" y1="13" x2="8" y2="13"/>
                            <line x1="16" y1="17" x2="8" y2="17"/>
                            <polyline points="10 9 9 9 8 9"/>
                        </svg>
                    </div>
                    <div className="action-text">
                        <h3>สรุปรายปี</h3>
                        <p>ดูสรุปรายได้ประจำปี {selectedYear + 543}</p>
                    </div>
                </Link>

                <Link to="/request" className="action-card">
                    <div className="action-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 12h6M9 16h6M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2z"/>
                        </svg>
                    </div>
                    <div className="action-text">
                        <h3>ขอเอกสาร</h3>
                        <p>ขอสลิปเงินเดือนหรือหนังสือ 50 ทวิ</p>
                    </div>
                </Link>
            </div>

            {/* Salary List */}
            <div className="steam-card">
                <div className="steam-card-header flex-between">
                    <span>ประวัติเงินเดือน ปี {selectedYear + 543}</span>
                    <span className="text-muted" style={{ fontSize: '13px' }}>
                        {salaries.filter(s => s.yyyy === selectedYear).length} รายการ
                    </span>
                </div>
                <div className="steam-card-body" style={{ padding: 0 }}>
                    {yearRecords.length === 0 ? (
                        <div className="empty-state">
                            <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="12" y1="8" x2="12" y2="12"/>
                                <line x1="12" y1="16" x2="12.01" y2="16"/>
                            </svg>
                            <p>ไม่พบข้อมูลเงินเดือนในปีนี้</p>
                            {/* แสดงปุ่มแจ้งปัญหาเมื่อไม่มีข้อมูล */}
                            {!issueReported && (
                                <button
                                    className="report-issue-btn"
                                    onClick={handleReportDataIssue}
                                    disabled={reportingIssue}
                                >
                                    {reportingIssue ? (
                                        <>
                                            <div className="spinner-small"></div>
                                            กำลังส่ง...
                                        </>
                                    ) : (
                                        <>
                                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                                <line x1="12" y1="9" x2="12" y2="13"/>
                                                <line x1="12" y1="17" x2="12.01" y2="17"/>
                                            </svg>
                                            ไม่พบข้อมูล? แจ้งผู้ดูแลระบบ
                                        </>
                                    )}
                                </button>
                            )}
                            {issueReported && (
                                <p className="issue-reported-msg">
                                    ส่งแจ้งปัญหาแล้ว - ผู้ดูแลระบบจะตรวจสอบให้
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className="salary-list">
                            {salaries
                                .filter(s => s.yyyy === selectedYear)
                                .sort((a, b) => b.mm - a.mm)
                                .map((salary) => (
                                    isPreparing(salary) ? (
                                        // เดือนนี้ยังไม่มีข้อมูลจริง — แสดงว่ากำลังจัดเตรียมข้อมูล (ไม่กดเข้าไป)
                                        <div
                                            key={`${salary.yyyy}-${salary.mm}`}
                                            className="salary-item salary-item-preparing"
                                        >
                                            <div className="salary-month">
                                                <span className="month-name">{thaiMonthsFull[salary.mm]}</span>
                                                <span className="month-year">{salary.yyyy + 543}</span>
                                            </div>
                                            <div className="salary-preparing">
                                                <span className="prep-dots" aria-hidden="true"><i></i><i></i><i></i></span>
                                                <div className="prep-text">
                                                    <span className="prep-title">กำลังจัดเตรียมข้อมูล</span>
                                                    <span className="prep-sub">เจ้าหน้าที่กำลังบันทึกข้อมูลเดือนนี้</span>
                                                    <span className="prep-note">โดยปกติจะเสร็จสิ้นภายในสัปดาห์แรกของเดือน</span>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <Link
                                            key={`${salary.yyyy}-${salary.mm}`}
                                            to={`/salary/${salary.yyyy}/${salary.mm}`}
                                            className="salary-item"
                                        >
                                            <div className="salary-month">
                                                <span className="month-name">{thaiMonthsFull[salary.mm]}</span>
                                                <span className="month-year">{salary.yyyy + 543}</span>
                                            </div>
                                            <div className="salary-details">
                                                <div className="salary-row">
                                                    <span className="label">รายรับ:</span>
                                                    <span className="value text-success">{formatNumber(getIncome(salary))}</span>
                                                </div>
                                                <div className="salary-row">
                                                    <span className="label">รายหัก:</span>
                                                    <span className="value text-danger">{formatNumber(getDeduction(salary))}</span>
                                                </div>
                                            </div>
                                            <div className="salary-net">
                                                <span className="net-label">รับสุทธิ</span>
                                                <span className="net-value">{formatNumber(getNetPay(salary))}</span>
                                                <span className="net-unit">บาท</span>
                                            </div>
                                            <div className="salary-arrow">
                                                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="9,18 15,12 9,6"/>
                                                </svg>
                                            </div>
                                        </Link>
                                    )
                                ))
                            }
                        </div>
                    )}
                </div>
            </div>

            <ReviewWidget />

            {/* แชร์สรุปรายได้ประจำปี (จากการ์ดรายรับรวม) */}
            {showYearShare && yearlyData && (
                <ShareSlipModal
                    mode="yearly"
                    data={buildYearlyShareData(yearlyData)}
                    yyyy={selectedYear}
                    mm={0}
                    onClose={() => setShowYearShare(false)}
                />
            )}
        </div>
    );
}

export default Dashboard;
